import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import Layout from '../components/Layout';
import DataTable from '../components/DataTable';
import Confirm from '../components/Confirm';
import Toast from '../components/Toast';
import { tiposMovimientoService, tablaService } from '../services/api';
import { getErrorDetail } from '../utils/errorHelper';
import '../styles/datatable.css';

const TIPO_OPTIONS   = [{ value: 'ENTRADA', label: 'Entrada' }, { value: 'SALIDA', label: 'Salida' }];
const COSTEO_OPTIONS = [
  { value: 'MANUAL',      label: 'Manual' },
  { value: 'PROMEDIO',    label: 'Promedio' },
  { value: 'NO_VALORIZA', label: 'No Valoriza' },
];
const MONEDA_OPTIONS = [
  { value: 'PEN', label: 'Soles (PEN)' },
  { value: 'USD', label: 'Dólares (USD)' },
];

const COSTEO_LABEL = { MANUAL: 'Manual', PROMEDIO: 'Promedio', NO_VALORIZA: 'No Valoriza' };
const TIPO_COLORS  = {
  ENTRADA: { color: '#22c55e', bg: 'rgba(34,197,94,0.12)' },
  SALIDA:  { color: '#fb923c', bg: 'rgba(251,146,60,0.12)' },
};

// label   = nombre del campo exactamente como aparece en el formulario de Parte
// section = sección del formulario donde vive el campo
const OBLIGATORIOS = [
  { key: 'obliga_ruc',             label: 'RUC / Proveedor',           section: 'Proveedor / Referencias' },
  { key: 'obliga_solicitante',     label: 'Solicitante',               section: 'Datos Adicionales'       },
  { key: 'obliga_centro_costo',    label: 'Centro de Costo',           section: 'Datos Adicionales'       },
  { key: 'obliga_doc_ref1',        label: 'Doc. Ref. 1',               section: 'Proveedor / Referencias' },
  { key: 'obliga_num_ref1',        label: 'Nº Ref. 1',                 section: 'Proveedor / Referencias' },
  { key: 'obliga_doc_ref2',        label: 'Doc. Ref. 2',               section: 'Proveedor / Referencias' },
  { key: 'obliga_num_ref2',        label: 'Nº Ref. 2',                 section: 'Proveedor / Referencias' },
  { key: 'obliga_glosa',           label: 'Observaciones / Glosa',     section: 'Datos Adicionales'       },
  { key: 'obliga_motivo_traslado', label: 'Motivo de Traslado',        section: 'Guía de Remisión'        },
];

const EMPTY_FORM = {
  codigo: '', descripcion: '', tipo: 'ENTRADA', tipo_costeo: 'MANUAL',
  id_tipo_operacion: '', moneda_defecto: 'PEN',
  obliga_ruc: false, obliga_solicitante: false, obliga_centro_costo: false,
  obliga_doc_ref1: false, obliga_num_ref1: false, obliga_doc_ref2: false,
  obliga_num_ref2: false, obliga_glosa: false, obliga_motivo_traslado: false,
};

// ── Custom Select con flecha profesional ────────────────────────────────────
const Sel = ({ value, onChange, children, disabled }) => (
  <div style={{ position: 'relative' }}>
    <select
      value={value}
      onChange={onChange}
      disabled={disabled}
      style={{
        width: '100%', background: '#0f1b2d', border: '1px solid rgba(96,165,250,0.22)',
        borderRadius: 7, padding: '7px 32px 7px 10px', color: '#e2e8f0', fontSize: 12.5,
        outline: 'none', boxSizing: 'border-box', cursor: 'pointer',
        appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
      }}
    >
      {children}
    </select>
    <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#60a5fa' }}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </div>
  </div>
);

const OptStyle = { background: '#0f1b2d', color: '#e2e8f0' };

// ── Componente principal ────────────────────────────────────────────────────
const TiposMovimiento = () => {
  const [records, setRecords]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [tiposOp, setTiposOp]     = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState(null);
  const [confirm, setConfirm]     = useState(null);
  const [toast, setToast]         = useState(null);

  const opServiceRef = useRef(tablaService('tipos-operacion'));

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [recs, ops] = await Promise.all([
        tiposMovimientoService.getAll(),
        opServiceRef.current.getAll(),
      ]);
      setRecords(recs.data);
      setTiposOp(ops.data);
    } catch (err) {
      setError(getErrorDetail(err, 'Error al cargar datos.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openCreate = () => { setForm(EMPTY_FORM); setEditing(null); setError(null); setShowModal(true); };
  const openEdit   = (r) => {
    setForm({
      codigo: r.codigo ?? '', descripcion: r.descripcion ?? '',
      tipo: r.tipo ?? 'ENTRADA', tipo_costeo: r.tipo_costeo ?? 'MANUAL',
      id_tipo_operacion: r.id_tipo_operacion ?? '',
      moneda_defecto: r.moneda_defecto ?? 'PEN',
      obliga_ruc: !!r.obliga_ruc, obliga_solicitante: !!r.obliga_solicitante,
      obliga_centro_costo: !!r.obliga_centro_costo, obliga_doc_ref1: !!r.obliga_doc_ref1,
      obliga_num_ref1: !!r.obliga_num_ref1, obliga_doc_ref2: !!r.obliga_doc_ref2,
      obliga_num_ref2: !!r.obliga_num_ref2, obliga_glosa: !!r.obliga_glosa,
      obliga_motivo_traslado: !!r.obliga_motivo_traslado,
    });
    setEditing(r);
    setError(null);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const codigo      = form.codigo.trim().toUpperCase();
    const descripcion = form.descripcion.trim();
    if (!codigo)            { setError('El código es obligatorio.');                        return; }
    if (codigo.length > 2)  { setError('El código debe tener máximo 2 caracteres.');        return; }
    if (!descripcion)       { setError('La descripción es obligatoria.');                   return; }
    if (descripcion.length > 30) { setError('La descripción debe tener máximo 30 caracteres.'); return; }

    setSaving(true); setError(null);
    try {
      const payload = {
        ...form,
        codigo,
        descripcion,
        id_tipo_operacion: form.id_tipo_operacion !== '' ? Number(form.id_tipo_operacion) : null,
      };
      if (editing) {
        await tiposMovimientoService.update(editing.id_tipo_movimiento, payload);
      } else {
        await tiposMovimientoService.create(payload);
      }
      setShowModal(false);
      fetchAll();
    } catch (err) {
      setError(getErrorDetail(err, 'Error al guardar.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (r) => {
    setConfirm({
      title: 'Eliminar Tipo de Movimiento',
      message: `¿Eliminar "${r.descripcion}"?`,
      onConfirm: async () => {
        setConfirm(null);
        try {
          await tiposMovimientoService.delete(r.id_tipo_movimiento);
          setToast('Tipo de movimiento eliminado');
          fetchAll();
        } catch { setToast('Error al eliminar'); }
      },
    });
  };

  // ── Columnas — render recibe row completo ──────────────────────────────
  const columns = [
    { key: 'codigo',      label: 'Código', width: '70px' },
    { key: 'descripcion', label: 'Descripción' },
    {
      key: 'tipo', label: 'Tipo', width: '100px',
      render: (row) => {
        const c = TIPO_COLORS[row.tipo] || {};
        return (
          <span style={{ background: c.bg, color: c.color, borderRadius: 12, padding: '2px 10px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
            {row.tipo}
          </span>
        );
      },
    },
    {
      key: 'tipo_costeo', label: 'Costeo', width: '110px',
      render: (row) => COSTEO_LABEL[row.tipo_costeo] || row.tipo_costeo,
    },
    { key: 'tipo_operacion_descripcion', label: 'Tipo Operación' },
    {
      key: 'moneda_defecto', label: 'Moneda', width: '80px',
      render: (row) => <span style={{ color: '#60a5fa', fontWeight: 600 }}>{row.moneda_defecto}</span>,
    },
  ];

  return (
    <Layout>
      <div className="page-header">
        <h2>Tipos de Movimiento</h2>
        <button className="btn btn-primary" onClick={openCreate}>+ Nuevo Tipo</button>
      </div>

      <DataTable
        data={records}
        columns={columns}
        idField="id_tipo_movimiento"
        loading={loading}
        onEdit={openEdit}
        onDelete={handleDelete}
        emptyMessage="No hay tipos de movimiento registrados"
      />

      {confirm && <Confirm {...confirm} onCancel={() => setConfirm(null)} />}
      {toast   && <Toast message={toast} onClose={() => setToast(null)} />}

      {/* ── Modal ── */}
      {showModal && ReactDOM.createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
          <div style={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 14, width: 'min(680px,96vw)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 48px rgba(0,0,0,0.5)', overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}>

            {/* Cabecera */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px 12px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#e2e8f0' }}>
                {editing ? 'Editar Tipo de Movimiento' : 'Nuevo Tipo de Movimiento'}
              </div>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 22, cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}>×</button>
            </div>

            <form onSubmit={handleSubmit} style={{ overflowY: 'auto', flex: 1, padding: '16px 20px', display: 'flex', flexDirection: 'column' }}>

              {/* ── Sección: Datos Generales ── */}
              <SectionTitle first>Datos Generales</SectionTitle>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 16px' }}>

                <Field label="Código *" w={80}>
                  <input
                    maxLength={2} value={form.codigo} required
                    onChange={e => set('codigo', e.target.value.toUpperCase())}
                    style={inputStyle} placeholder="XX"
                  />
                </Field>

                <Field label="Descripción *" w={360}>
                  <input
                    maxLength={30} value={form.descripcion} required
                    onChange={e => set('descripcion', e.target.value.toUpperCase())}
                    style={inputStyle} placeholder="MÁX. 30 CARACTERES"
                  />
                </Field>

                <Field label="Tipo *" w={130}>
                  <Sel value={form.tipo} onChange={e => set('tipo', e.target.value)}>
                    {TIPO_OPTIONS.map(o => <option key={o.value} value={o.value} style={OptStyle}>{o.label}</option>)}
                  </Sel>
                </Field>

                <Field label="Tipo Costeo *" w={150}>
                  <Sel value={form.tipo_costeo} onChange={e => set('tipo_costeo', e.target.value)}>
                    {COSTEO_OPTIONS.map(o => <option key={o.value} value={o.value} style={OptStyle}>{o.label}</option>)}
                  </Sel>
                </Field>

                <Field label="Tipo Operación" w={220}>
                  <Sel value={form.id_tipo_operacion} onChange={e => set('id_tipo_operacion', e.target.value)}>
                    <option value="" style={OptStyle}>— Ninguno —</option>
                    {tiposOp.map(op => (
                      <option key={op.id_tipo_operacion} value={op.id_tipo_operacion} style={OptStyle}>
                        {op.codigo} — {op.descripcion}
                      </option>
                    ))}
                  </Sel>
                </Field>

                <Field label="Moneda por defecto *" w={160}>
                  <Sel value={form.moneda_defecto} onChange={e => set('moneda_defecto', e.target.value)}>
                    {MONEDA_OPTIONS.map(o => <option key={o.value} value={o.value} style={OptStyle}>{o.label}</option>)}
                  </Sel>
                </Field>

              </div>

              {/* ── Sección: Campos Obligatorios ── */}
              <SectionTitle>Campos obligatorios en el formulario de Parte</SectionTitle>
              <p style={{ fontSize: 11, color: '#64748b', margin: '-4px 0 10px', lineHeight: 1.5 }}>
                Marca los campos que deben completarse obligatoriamente al registrar un parte con este tipo de movimiento.
                El nombre de cada campo corresponde exactamente al campo del formulario.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', paddingBottom: 8 }}>
                {OBLIGATORIOS.map(ob => (
                  <label key={ob.key} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none', padding: '6px 10px', borderRadius: 7, border: `1px solid ${form[ob.key] ? 'rgba(96,165,250,0.35)' : 'rgba(255,255,255,0.06)'}`, background: form[ob.key] ? 'rgba(96,165,250,0.08)' : 'rgba(255,255,255,0.02)', transition: 'all .15s' }}>
                    <input
                      type="checkbox"
                      checked={!!form[ob.key]}
                      onChange={e => set(ob.key, e.target.checked)}
                      style={{ accentColor: '#60a5fa', width: 14, height: 14, cursor: 'pointer', flexShrink: 0 }}
                    />
                    <div>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: form[ob.key] ? '#93c5fd' : '#cbd5e1' }}>{ob.label}</div>
                      <div style={{ fontSize: 10, color: '#475569', marginTop: 1 }}>Sección: {ob.section}</div>
                    </div>
                  </label>
                ))}
              </div>

              {error && (
                <div style={{ marginTop: 8, background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 8, padding: '8px 12px', fontSize: 12.5, color: '#fca5a5' }}>
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Guardando...' : editing ? 'Guardar cambios' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </Layout>
  );
};

/* ── Helpers ── */
const inputStyle = {
  width: '100%', background: '#0f1b2d', border: '1px solid rgba(96,165,250,0.22)',
  borderRadius: 7, padding: '7px 10px', color: '#e2e8f0', fontSize: 12.5,
  outline: 'none', boxSizing: 'border-box',
};

const SectionTitle = ({ children, first }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: first ? '0 0 10px' : '14px 0 10px' }}>
    <span style={{ fontSize: 9.5, fontWeight: 700, color: '#60a5fa', textTransform: 'uppercase', letterSpacing: '1px', whiteSpace: 'nowrap' }}>{children}</span>
    <div style={{ flex: 1, height: 1, background: 'rgba(96,165,250,0.2)' }} />
  </div>
);

const Field = ({ label, w, children }) => (
  <div style={{ flex: `0 0 ${w}px`, minWidth: 0, paddingBottom: 10 }}>
    <div style={{ fontSize: 9.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 4 }}>{label}</div>
    {children}
  </div>
);

export default TiposMovimiento;
